// ============================================================
// CommunityRoles — MEN-237 [V1-06] : gestion des rôles d'une
// communauté (liste hiérarchique + drag-drop reorder natif +
// éditeur inline nom/couleur/permissions).
//
// Slice 1 : acceptance #1 (reorder persiste -> PATCH position) et
// acceptance #2 (edit perms = optimistic update + rollback si 4xx).
// L'assignation de membres + l'éditeur ACL par salon + le preview
// "effective perms" (acceptance #3, nécessite une nouvelle surface
// API GET /channels/:id/permissions?userId=X) sont livrés dans un
// suivi dédié.
//
// Toute la donnée reste serveur : le bitmask `permissions` est une
// String décimale BigInt-safe stockée en DB et jamais recalculée
// côté client — l'éditeur ne fait que basculer des bits du masque
// déclaré du rôle, le serveur applique l'anti-escalade. Mises à
// jour temps réel via Socket.IO (role:created/updated/deleted).
//
// [2.13.1] « Enregistrer » n'envoie que les champs modifiés, et un rôle changé
// ailleurs pendant l'édition (bureau, autre console) recale le brouillon : les
// champs pas touchés prennent la nouvelle valeur (roleEditPatch /
// rebaseRoleEdit, @siverix/shared). Seuls les événements de CE serveur Siverix
// relisent la liste. « Aucune couleur » se choisit (plus de noir par défaut).
// @everyone : seules ses permissions se règlent (son nom, sa couleur,
// « afficher séparément » et « mentionnable » sont ignorés, ici comme par l'API).
// ============================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  EDITABLE_ROLE_FLAGS,
  PERMISSION_INFO,
  Permission,
  ROLE_COLOR_PRESETS,
  ROLE_PERMISSION_GROUPS,
  colorIntToHex,
  rebaseRoleEdit,
  roleEditOf,
  roleEditPatch,
  sameRoleEdit,
  type RoleEdit,
} from '@siverix/shared';
import type { AdminRole } from '@siverix/api-client';
import { ApiError } from '@siverix/api-client';
import { api } from '../api';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-border)',
};
const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};
const inp: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 6,
  border: '1px solid var(--color-accent)',
  background: 'var(--color-accent)',
  color: '#fff',
  fontSize: '13px',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontSize: '12px',
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  ...btnGhost,
  borderColor: 'var(--color-danger, #c0392b)',
  color: 'var(--color-danger, #c0392b)',
};

// [2.12] Permissions présentées : celles de l'éditeur partagé, groupées et en
// français (packages/shared/src/roles.ts). Le bit 30 (administration de
// l'instance) et les permissions que l'API n'applique pas ne sont pas
// proposés ; leur valeur actuelle est conservée à l'enregistrement.
const EDITABLE_COUNT = EDITABLE_ROLE_FLAGS.length;

function hexToColorInt(hex: string): number {
  const v = parseInt(hex.replace(/^#/, ''), 16);
  return Number.isFinite(v) ? v : 0;
}
function permBits(role: AdminRole): bigint {
  try {
    return BigInt(role.permissions);
  } catch {
    return 0n;
  }
}
function countPerms(bits: bigint): number {
  let c = 0;
  for (const flag of EDITABLE_ROLE_FLAGS) if ((bits & Permission[flag]) !== 0n) c++;
  return c;
}

/** Rôle ouvert dans l'éditeur : tel qu'enregistré (`base`) et tel que modifié (`draft`). */
interface Editor {
  id: string;
  base: RoleEdit;
  draft: RoleEdit;
}

/**
 * Rôles relus (ou réponse d'un enregistrement) : si le rôle ouvert a changé en
 * base, le brouillon suit pour les champs pas touchés.
 */
function followSaved(ed: Editor | null, list: readonly AdminRole[]): Editor | null {
  if (!ed) return ed;
  const role = list.find((r) => r.id === ed.id);
  if (!role) return ed;
  const next = roleEditOf(role);
  if (sameRoleEdit(ed.base, next)) return ed;
  return { ...ed, base: next, draft: rebaseRoleEdit(ed.draft, ed.base, next) };
}

/** Couleur reprise quand on décoche « Aucune couleur ». */
const DEFAULT_ROLE_COLOR = ROLE_COLOR_PRESETS[2] ?? 0x3498db;

/** Événement de rôle : serveur Siverix concerné, quand il est annoncé. */
function eventCommunityId(d: unknown): string | undefined {
  if (!d || typeof d !== 'object') return undefined;
  const e = d as { role?: { communityId?: unknown }; communityId?: unknown };
  const cid = e.role?.communityId ?? e.communityId;
  return typeof cid === 'string' ? cid : undefined;
}

/** Pastille de la liste : hachurée pour un rôle sans couleur (au lieu du noir). */
function ColorSwatch({ color }: { color: number }): JSX.Element {
  const none = color === 0;
  return (
    <span
      title={none ? 'Aucune couleur' : colorIntToHex(color)}
      data-none={none ? '' : undefined}
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: 3,
        background: none
          ? 'repeating-linear-gradient(45deg, transparent 0 2px, var(--color-border) 2px 4px)'
          : colorIntToHex(color),
        border: none ? '1px dashed var(--color-border)' : '1px solid var(--color-border)',
        marginRight: 8,
        verticalAlign: 'middle',
      }}
    />
  );
}

export function CommunityRoles(): JSX.Element {
  const { id: communityId } = useParams<{ id: string }>();
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const selectedId = editor?.id ?? null;
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const dragId = useRef<string | null>(null);
  const { socket } = useSocket();

  async function fetchAll(): Promise<void> {
    if (!communityId) return;
    try {
      const list = await api.adminListRoles(communityId);
      setRoles(list);
      setEditor((ed) => followSaved(ed, list));
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  useEffect(() => {
    if (!socket || !communityId) return;
    // Seulement les rôles de ce serveur Siverix (le socket reçoit ceux des autres).
    const refresh = (d?: unknown): void => {
      const cid = eventCommunityId(d);
      if (cid !== undefined && cid !== communityId) return;
      void fetchAll();
    };
    socket.on('role:created', refresh);
    socket.on('role:updated', refresh);
    socket.on('role:deleted', refresh);
    return () => {
      socket.off('role:created', refresh);
      socket.off('role:updated', refresh);
      socket.off('role:deleted', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, communityId]);

  // Affichage hiérarchique : position DESC (le plus haut = position max).
  const ordered = useMemo(
    () => [...roles].sort((a, b) => b.position - a.position || a.name.localeCompare(b.name)),
    [roles],
  );
  const selected = ordered.find((r) => r.id === selectedId) ?? null;
  const draft = editor?.draft ?? null;
  // [2.13.1] Ce qui partirait : seulement les champs modifiés (@everyone : ses permissions).
  const patch =
    editor && selected ? roleEditPatch(editor.draft, editor.base, selected.isManaged) : {};
  const dirty = Object.keys(patch).length > 0;
  const nameMissing = !!draft && !!selected && !selected.isManaged && draft.name.trim() === '';

  function openEditor(r: AdminRole): void {
    const saved = roleEditOf(r);
    setEditor({ id: r.id, base: saved, draft: saved });
    setSavedFlash(false);
  }

  function closeEditor(): void {
    setEditor(null);
  }

  function setDraft(change: Partial<RoleEdit>): void {
    setEditor((ed) => (ed ? { ...ed, draft: { ...ed.draft, ...change } } : ed));
  }

  function toggleBit(bit: bigint): void {
    setEditor((ed) => {
      if (!ed) return ed;
      const p = ed.draft.permissions;
      return { ...ed, draft: { ...ed.draft, permissions: (p & bit) !== 0n ? p & ~bit : p | bit } };
    });
  }

  // Acceptance #2 : edit perms -> save instantané, optimistic update,
  // rollback si 4xx (refetch depuis le serveur = source de vérité).
  // [2.13.1] Seuls les champs modifiés partent : un changement fait ailleurs
  // sur un autre champ, entre l'ouverture et l'enregistrement, est gardé.
  async function saveDraft(): Promise<void> {
    if (!communityId || !selected || !editor || !dirty || nameMissing) return;
    const prev = roles;
    const sent = patch;
    setRoles((rs) => rs.map((r) => (r.id === selected.id ? { ...r, ...sent } : r)));
    setSaving(true);
    try {
      const fresh = await api.adminUpdateRole(communityId, selected.id, sent);
      setRoles((rs) => rs.map((r) => (r.id === fresh.id ? fresh : r)));
      setEditor((ed) => followSaved(ed, [fresh]));
      setSavedFlash(true);
      setErr(null);
    } catch (e) {
      setRoles(prev); // rollback
      const msg =
        e instanceof ApiError ? `Échec (${e.status}) : ${e.message}` : (e as Error).message;
      setErr(msg);
    } finally {
      setSaving(false);
    }
  }

  async function createRole(): Promise<void> {
    if (!communityId) return;
    const name = window.prompt('Nom du nouveau rôle ?');
    if (!name?.trim()) return;
    try {
      const maxPos = roles.reduce((m, r) => Math.max(m, r.position), 0);
      const r = await api.adminCreateRole(communityId, {
        name: name.trim(),
        position: maxPos + 1,
      });
      await fetchAll();
      openEditor(r);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function deleteRole(r: AdminRole): Promise<void> {
    if (!communityId || r.isManaged) return;
    if (!window.confirm(`Supprimer le rôle « ${r.name} » ?`)) return;
    try {
      await api.adminDeleteRole(communityId, r.id);
      if (selectedId === r.id) closeEditor();
      await fetchAll();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  // Acceptance #1 : reorder visuel -> PATCH position pour chaque rôle
  // déplacé. Optimistic ; rollback (refetch) si un PATCH échoue.
  // @everyone (isManaged) reste épinglé en bas, non déplaçable.
  async function handleDrop(targetId: string): Promise<void> {
    const srcId = dragId.current;
    dragId.current = null;
    if (!communityId || !srcId || srcId === targetId) return;
    const movable = ordered.filter((r) => !r.isManaged);
    const from = movable.findIndex((r) => r.id === srcId);
    const to = movable.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...movable];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    // Top de la liste = position la plus haute (> 0, jamais en conflit
    // avec @everyone seedé en position 0).
    const newPos = new Map<string, number>();
    next.forEach((r, i) => newPos.set(r.id, next.length - i));
    const changed = next.filter((r) => newPos.get(r.id) !== r.position);
    if (changed.length === 0) return;

    const prev = roles;
    setRoles((rs) => rs.map((r) => (newPos.has(r.id) ? { ...r, position: newPos.get(r.id)! } : r)));
    try {
      for (const r of changed) {
        await api.adminUpdateRole(communityId, r.id, { position: newPos.get(r.id)! });
      }
      setErr(null);
    } catch (e) {
      setRoles(prev); // rollback
      setErr(`Échec du réordonnancement : ${(e as Error).message}`);
      void fetchAll();
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ marginTop: 0 }}>Rôles & permissions</h1>
        <Link to="/communities" style={{ fontSize: 13, color: 'var(--color-accent)' }}>
          ← Communautés
        </Link>
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Glissez-déposez pour réordonner la hiérarchie (le plus haut prime). Cliquez un rôle pour
        éditer son nom, sa couleur et ses permissions. @everyone est protégé.
      </p>

      {err && <ErrorBox message={err} />}

      <div style={{ margin: '12px 0' }}>
        <button type="button" style={btn} onClick={() => void createRole()}>
          + Nouveau rôle
        </button>
      </div>

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Liste hiérarchique drag-drop */}
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Rôle</th>
                  <th style={th}>Perms</th>
                  <th style={th}>Flags</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r) => (
                  <tr
                    key={r.id}
                    draggable={!r.isManaged}
                    onDragStart={(e) => {
                      if (r.isManaged) return;
                      dragId.current = r.id;
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      if (dragId.current) e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      void handleDrop(r.id);
                    }}
                    onClick={() => openEditor(r)}
                    style={{
                      cursor: r.isManaged ? 'pointer' : 'grab',
                      background: selectedId === r.id ? 'var(--color-surface)' : 'transparent',
                    }}
                  >
                    <td style={td}>
                      <ColorSwatch color={r.isManaged ? 0 : r.color} />
                      <strong>{r.name}</strong>
                      {r.isManaged && (
                        <span
                          style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-text-muted)' }}
                        >
                          (système)
                        </span>
                      )}
                    </td>
                    <td style={td}>{countPerms(permBits(r))}</td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {r.isManaged
                        ? '—'
                        : [r.hoist ? 'hoist' : null, r.mentionable ? '@' : null]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        type="button"
                        style={btnDanger}
                        disabled={r.isManaged}
                        title={r.isManaged ? '@everyone protégé' : 'Supprimer'}
                        onClick={(e) => {
                          e.stopPropagation();
                          void deleteRole(r);
                        }}
                      >
                        Suppr.
                      </button>
                    </td>
                  </tr>
                ))}
                {ordered.length === 0 && (
                  <tr>
                    <td style={td} colSpan={4}>
                      Aucun rôle.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Éditeur inline */}
          {selected && draft && (
            <div
              style={{
                flex: '1 1 360px',
                minWidth: 300,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                padding: 16,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Éditer « {selected.name} »</h3>

              {selected.isManaged ? (
                <p
                  style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}
                  data-testid="role-editor-managed-note"
                >
                  Rôle de tous les membres : seules ses permissions se règlent. Son nom, sa couleur,
                  « afficher séparément » et « mentionnable » ne s’appliquent pas.
                </p>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={lbl} htmlFor="role-editor-name">
                      Nom
                    </label>
                    <input
                      id="role-editor-name"
                      style={inp}
                      value={draft.name}
                      maxLength={80}
                      onChange={(e) => setDraft({ name: e.target.value })}
                    />
                    {nameMissing && (
                      <span style={{ fontSize: 12, color: 'var(--color-danger, #c0392b)' }}>
                        Le rôle doit avoir un nom.
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      marginBottom: 12,
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <label style={lbl} htmlFor="role-editor-color">
                        Couleur
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          id="role-editor-color"
                          type="color"
                          value={colorIntToHex(draft.color || DEFAULT_ROLE_COLOR)}
                          disabled={draft.color === 0}
                          onChange={(e) => setDraft({ color: hexToColorInt(e.target.value) })}
                          style={{
                            width: 48,
                            height: 32,
                            padding: 0,
                            border: '1px solid var(--color-border)',
                            borderRadius: 6,
                            background: 'transparent',
                            cursor: draft.color === 0 ? 'not-allowed' : 'pointer',
                            opacity: draft.color === 0 ? 0.4 : 1,
                          }}
                        />
                        <label
                          style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}
                        >
                          <input
                            type="checkbox"
                            checked={draft.color === 0}
                            data-testid="role-editor-no-color"
                            onChange={(e) =>
                              setDraft({
                                color: e.target.checked
                                  ? 0
                                  : editor?.base.color || DEFAULT_ROLE_COLOR,
                              })
                            }
                          />
                          Aucune couleur
                        </label>
                      </div>
                    </div>
                    <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={draft.hoist}
                        onChange={(e) => setDraft({ hoist: e.target.checked })}
                      />
                      Afficher séparément (hoist)
                    </label>
                    <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={draft.mentionable}
                        onChange={(e) => setDraft({ mentionable: e.target.checked })}
                      />
                      Mentionnable
                    </label>
                  </div>
                </>
              )}

              <label style={lbl}>
                Permissions ({countPerms(draft.permissions)} / {EDITABLE_COUNT})
              </label>
              <div
                style={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  padding: 10,
                  marginBottom: 14,
                }}
              >
                {ROLE_PERMISSION_GROUPS.map((group) => (
                  <div key={group.id}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: 'var(--color-text-muted)',
                        margin: '6px 0 2px',
                      }}
                    >
                      {group.label}
                    </div>
                    <div
                      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}
                    >
                      {group.flags.map((flag) => {
                        const info = PERMISSION_INFO[flag];
                        const bit = Permission[flag];
                        return (
                          <label
                            key={flag}
                            title={info.warning ?? info.description}
                            style={{
                              fontSize: 12,
                              display: 'flex',
                              gap: 6,
                              alignItems: 'center',
                              cursor: 'pointer',
                              color: info.warning ? 'var(--color-danger, #c0392b)' : undefined,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(draft.permissions & bit) !== 0n}
                              onChange={() => toggleBit(bit)}
                            />
                            {info.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  style={{ ...btn, opacity: saving || !dirty || nameMissing ? 0.6 : 1 }}
                  disabled={saving || !dirty || nameMissing}
                  onClick={() => void saveDraft()}
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button type="button" style={btnGhost} onClick={closeEditor}>
                  Fermer
                </button>
                {savedFlash && (
                  <span style={{ fontSize: 12, color: 'var(--color-success, #2e7d32)' }}>
                    ✓ Enregistré
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
